import { ErrorHandler, Injectable, InjectionToken, inject, signal, untracked } from '@angular/core';
import type { CategoryNodeMap } from '../category/category-node-map';
import type { Membership } from './membership';
import { SegmentDefinition } from './segment';
import { SegmentEvaluatorService } from './segment-evaluator.service';

const UNRESOLVED: Membership = { status: 'unresolved' };
const FAILED: Membership = { status: 'failed' };

/** The category node map a definition is compiled against, available once the taxonomy loads. */
export interface SegmentMembershipConfig {
  readonly categoryNodeMap: () => Promise<CategoryNodeMap>;
}

export const SEGMENT_MEMBERSHIP_CONFIG = new InjectionToken<SegmentMembershipConfig>(
  'SEGMENT_MEMBERSHIP_CONFIG'
);

type CacheEntry = {
  readonly settled: Promise<void>;
  readonly resolution: Membership;
};

/**
 * The membership of segment definitions, evaluated once per authored content per session.
 *
 * domain-remodel ADR-0003 defines a segment's membership as its formula evaluated against the
 * live catalog, plus pins, minus excludes: `SegmentDefinition.toPredicate` in, members out. This
 * store is the one place the portal asks that question. Consumers read `membershipOf` in a
 * computed or a template, and the store starts evaluating a definition on its first read, so
 * nothing outside the store has to warm it and nothing here evaluates per render (HC-03).
 * `resolve` is for callers that must await an answer.
 *
 * Entries are keyed by `SegmentDefinition.authoredKey`, so segments sharing a definition share
 * one evaluation, and an edited definition is a new key. Alias and sharing are not part of the
 * definition, so a rename or a share toggle keeps the members. A failed entry is evaluated
 * again by the next `resolve` only, so a transient error does not stick for the session and a
 * template reading a failing definition does not re-evaluate it each pass.
 */
@Injectable()
export class SegmentMembershipStore {
  private readonly evaluator = inject(SegmentEvaluatorService);
  private readonly config = inject(SEGMENT_MEMBERSHIP_CONFIG);
  private readonly errorHandler = inject(ErrorHandler);

  private readonly entries = signal<ReadonlyMap<string, CacheEntry>>(new Map());
  // Definitions are immutable values, so a key hashed once per object serves every later read.
  private readonly keys = new WeakMap<SegmentDefinition, string>();
  private readonly scheduled = new Set<string>();

  /** What is known right now. Reads signals, and an unknown definition starts evaluating. */
  membershipOf(definition: SegmentDefinition): Membership {
    const key = this.keyOf(definition);
    const entry = this.entries().get(key);
    if (entry) return entry.resolution;
    if (!this.scheduled.has(key)) {
      this.scheduled.add(key);
      // A signal write inside a computed or template throws NG0600, so the start waits a microtask.
      queueMicrotask(() => {
        this.scheduled.delete(key);
        void this.settledFor(definition);
      });
    }
    return UNRESOLVED;
  }

  /** Evaluates each definition that has no answer yet; awaits them all (HC-03). */
  async resolve(definitions: readonly SegmentDefinition[]): Promise<void> {
    await Promise.all(definitions.map((d) => this.settledFor(d)));
  }

  private keyOf(definition: SegmentDefinition): string {
    let key = this.keys.get(definition);
    if (key === undefined) {
      key = SegmentDefinition.authoredKey(definition);
      this.keys.set(definition, key);
    }
    return key;
  }

  private settledFor(definition: SegmentDefinition): Promise<void> {
    const key = this.keyOf(definition);
    const entry = untracked(this.entries).get(key);
    if (entry && entry.resolution.status !== 'failed') return entry.settled;
    return this.startEvaluation(key, definition);
  }

  private startEvaluation(key: string, definition: SegmentDefinition): Promise<void> {
    const settled = this.evaluate(key, definition);
    // `evaluate` awaits before it settles, so this entry is in place by the time it writes.
    this.entries.update((entries) =>
      new Map(entries).set(key, { settled, resolution: UNRESOLVED })
    );
    return settled;
  }

  private async evaluate(key: string, definition: SegmentDefinition): Promise<void> {
    try {
      const nodeMap = await this.config.categoryNodeMap();
      const skus = await this.evaluator.evaluateWithBrand(
        SegmentDefinition.toPredicate(definition),
        nodeMap
      );
      this.settle(key, { status: 'resolved', skus });
    } catch (cause: unknown) {
      this.errorHandler.handleError(cause);
      this.settle(key, FAILED);
    }
  }

  private settle(key: string, resolution: Membership): void {
    this.entries.update((entries) => {
      const entry = entries.get(key);
      return entry ? new Map(entries).set(key, { ...entry, resolution }) : entries;
    });
  }
}
